import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import {
  Avatar,
  ClientLink,
  DateTime,
  ExportDropdown,
  InternalClientTooltip,
  MemberContactPopover,
  ProjectLink,
  RouteLink,
  YesNoFilter,
} from '~/components';
import ClientFiltersBar from '~/components/filters/ClientFiltersBar.jsx';
import ClientFiltersGroup from '~/components/filters/ClientFiltersGroup.jsx';
import FilterButton from '~/components/filters/FilterButton';
import MemberFiltersBar from '~/components/filters/MemberFiltersBar.jsx';
import MemberFiltersGroup from '~/components/filters/MemberFiltersGroup.jsx';
import ProjectFiltersBar from '~/components/filters/ProjectFiltersBar.jsx';
import ProjectFiltersGroup from '~/components/filters/ProjectFiltersGroup.jsx';
import ReportPeriodFilter from '~/components/filters/ReportPeriodFilter';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import { useDocumentTitle, useFeatures, useIsMounted, useSearchParams, useSearchParamsConfig } from '~/hooks';
import useClientFilters from '~/hooks/useClientFilters';
import useMemberFilters from '~/hooks/useMemberFilters.js';
import useProjectFilters from '~/hooks/useProjectFilters';
import ExpenseItemDrawer from '~/routes/app/expenses/item/ExpenseItemDrawer';
import EditTimeEntry from '~/routes/app/time/edit-time-entry';
import ViewTimeEntry from '~/routes/app/time/view-time-entry';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import { QuerySort, intervalOptions } from '~/utils';
import mimeTypes from '~/utils/mimeTypes';
import ProjectTaskForm from '../../project-tasks/ProjectTaskForm';
import AllocationForm from '../../resources/allocations/dialogs/AllocationForm';
import SecurityRoleForm from '../../settings/security-roles/SecurityRoleForm';
import ExportDialogAsync from '../components/ExportDialogAsync.jsx';
import HistoryEntityFilter from '../components/HistoryEntityFilter';
import HistoryEventFilter from '../components/HistoryEventFilter';
import HistorySourceFilter from '../components/HistorySourceFilter';
import Report from '../components/Report';
import Table from '../components/table';
import useReportsSearchParamsConfig from '../hooks/useReportsSearchParamsConfig';
import { entities, events, sources } from '../lookups';
import ReportActions from '../ReportActions';

const DetailsInfo = styled.div`
  line-height: 1;

  small {
    font-size: 0.75rem;
    color: ${colors.grey40};
    display: flex;
    padding-top: 0.25rem;
  }
`;

const HistoryNotes = styled.p`
  flex-basis: 100%;
  padding: 0 4rem 1rem 13rem;
  color: ${colors.grey55};
  font-size: 0.75rem;
  &:before {
    content: open-quote;
  }

  &:after {
    content: close-quote;
  }
`;

const Member = styled.div`
  display: flex;
  align-items: center;
`;

const MemberInfo = styled.span`
  margin-left: 0.5rem;
`;

function History({ report }) {
  const documentTitle = useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();
  const history = useHistory();

  const isMounted = useIsMounted();

  const memberFilters = useMemberFilters();
  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters();

  const [query, setQuery] = useState({
    report: null,
    params: {
      period: null,
      entities: [],
      events: [],
      sources: [],
      showNotes: 'no',
      sort: new QuerySort('eventTime', 'asc'),
      page: 0,
      size: 50,
      ...memberFilters.filters,
      ...clientFilters.filters,
      ...projectFilters.filters,
    },
    action: 'load',
    status: 'loading',
    loading: {
      table: false,
    },
  });

  const params = query.params;

  const setParams = (params) => {
    setQuery((state) => ({
      ...state,
      params: { ...state.params, ...params },
    }));
  };

  const loadMore = () => {
    setQuery((state) => {
      if (
        state.status !== 'ready' ||
        state.action !== null ||
        !state.report ||
        state.report.records.total <= state.report.records.results.length
      )
        return state;

      return {
        ...state,
        params: { ...state.params, page: state.params.page + 1 },
        action: 'load-more',
      };
    });
  };

  const searchParamsConfig = useSearchParamsConfig();
  const reportsSearchParamsConfig = useReportsSearchParamsConfig();
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: { ...reportsSearchParamsConfig.period, default: intervalOptions.past_7_days },
        entities: {
          multi: true,
          key: 'entity',
          valid: _.map(entities, (v) => v.id),
          serialize: (value) => value?.map((v) => v.id),
          deserialize: (ids) => ids.map((id) => entities[id]),
        },

        events: {
          multi: true,
          key: 'event',
          valid: _.map(events, (v) => v.id),
          serialize: (value) => value?.map((v) => v.id),
          deserialize: (ids) => ids.map((id) => events[id]),
        },

        sources: {
          multi: true,
          key: 'source',
          valid: _.map(sources, (v) => v.id),
          serialize: (value) => value?.map((v) => v.id),
          deserialize: (ids) => ids.map((id) => sources[id]),
        },
        sort: { default: new QuerySort('eventTime', 'desc'), ...searchParamsConfig.sort },
        showNotes: { default: 'no', valid: ['yes', 'no'] },
        ...memberFilters.searchParamsConfig,
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
      }),
      [memberFilters, clientFilters, projectFilters, searchParamsConfig, reportsSearchParamsConfig],
    ),

    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  const urlSearchParams = useMemo(
    () => ({
      entity: params.entities?.map((v) => v.id),
      event: params.events?.map((v) => v.id),
      source: params.sources?.map((v) => v.id),
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      sort: params.sort,
      page: params.page,
      size: params.size,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...memberFilters.mapUrlSearchParams(params),
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
    }),
    [params, memberFilters, clientFilters, projectFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams(params);
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);

    if (!isMounted.current) return;

    setQuery((state) => ({
      ...state,
      action: null,
      status: 'ready',
      loading: { table: false },
      report: {
        ...data,
        records: {
          ...data.records,
          results:
            state.action === 'load-more'
              ? [...state.report.records.results, ...data.records.results]
              : data.records.results,
        },
      },
    }));
  }, [api, workspace.id, report.id, urlSearchParams, isMounted]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [dialog, setDialog] = useState(null);

  const handleRowClick = async (item) => {
    switch (item.entity) {
      case 'allocation':
        setDialog(
          <AllocationForm
            allocationId={item.entityId}
            onSaved={handleRowChanged}
            onDeleted={handleRowChanged}
            onClose={handleCloseDrawer}
          />,
        );
        break;
      case 'client':
        history.push(`/app/${workspace.key}/clients/${item.entityId}/overview`);
        break;
      case 'client_approval':
        history.push(
          `/app/${workspace.key}/projects/${item.project.client.key}/${item.project.key}/client-approvals/${item.entityId}`,
        );
        break;
      case 'credit_note':
        history.push(`/app/${workspace.key}/billing/credit-notes/${item.entityId}`);
        break;
      case 'expense_item':
        setDialog(
          <ExpenseItemDrawer
            expenseItemId={item.entityId}
            onSaved={handleRowChanged}
            onDeleted={handleRowChanged}
            onClose={handleCloseDrawer}
          />,
        );
        break;
      case 'invoice':
        history.push(`/app/${workspace.key}/billing/invoices/${item.entityId}`);
        break;
      case 'member':
        history.push(`/app/${workspace.key}/settings/members/details/${item.entityId}`);
        break;
      case 'project':
        history.push(`/app/${workspace.key}/projects/${item.project.client.key}/${item.project.key}/overview`);
        break;
      case 'project_task':
        try {
          const { data } = await api.www.workspaces(workspace.id).projects(item.project.id).get();

          setDialog(
            <ProjectTaskForm
              taskId={item.entityId}
              project={data}
              onSaved={handleRowChanged}
              onClose={handleCloseDrawer}
            />,
          );
        } catch {
          // Do nothing
        }
        break;
      case 'security_role':
        setDialog(
          <SecurityRoleForm securityRoleId={item.entityId} onSaved={handleRowChanged} onClose={handleCloseDrawer} />,
        );
        break;
      case 'time_entry':
        setDialog(
          item.timeEntry.isLocked ? (
            <ViewTimeEntry id={item.entityId} onClose={handleCloseDrawer} />
          ) : (
            <EditTimeEntry
              id={item.entityId}
              onSaved={handleRowChanged}
              onDeleted={handleRowChanged}
              onClose={handleCloseDrawer}
            />
          ),
        );
        break;
      default:
        return null;
    }
  };

  const handleCloseDrawer = () => {
    setDialog(null);
    documentTitle.set(report.name);
  };

  const handleRowChanged = async () => {
    const { data } = await api.www
      .workspaces(workspace.id)
      .reports(report.id)
      .data({
        ...urlSearchParams,
        page: 0,
        // Refetch every loaded page
        size: (query.params.page + 1) * query.params.size,
      });

    if (!isMounted.current) return;

    setQuery((state) => ({
      ...state,
      report: data,
    }));

    handleCloseDrawer();
  };

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values, page: 0 });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSort = ({ column, sort }) => {
    setQuery((state) => ({ ...state, loading: { ...state.loading, table: true } }));
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ ...params, page: 0, sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data({ ...urlSearchParams, page: undefined, size: undefined }, { headers: { accept: mimeType } })}
        onClose={resolve}
      />
    ));
  };

  return (
    <>
      <Report>
        <Report.Header>
          <Report.Info report={report} />

          <Report.Actions>
            <ReportActions onSave={() => searchParams.set(params)} />
            <ExportDropdown>
              {({ setIsOpen }) => (
                <>
                  <ExportDropdown.Item
                    onClick={async () => {
                      await handleExport(mimeTypes.csv);
                      setIsOpen(false);
                    }}>
                    Export to CSV
                  </ExportDropdown.Item>

                  <ExportDropdown.Item
                    onClick={async () => {
                      await handleExport(mimeTypes.xlsx);
                      setIsOpen(false);
                    }}>
                    Export to Excel
                  </ExportDropdown.Item>
                </>
              )}
            </ExportDropdown>

            <FilterButton isOutline onClick={showFilters} />
          </Report.Actions>
        </Report.Header>

        <Report.FiltersBar>
          <ReportPeriodFilter
            placeholder="Event Date Range"
            value={params.period}
            onChange={({ target: { value } }) => handleApplyFilters({ period: value })}
          />

          {!_.isEmpty(params.entities) && (
            <HistoryEntityFilter
              materialPlaceholder="Entity"
              value={params.entities}
              onChange={({ target: { value } }) => handleApplyFilters({ entities: value })}
            />
          )}

          {!_.isEmpty(params.events) && (
            <HistoryEventFilter
              materialPlaceholder="Event"
              value={params.events}
              onChange={({ target: { value } }) => handleApplyFilters({ events: value })}
            />
          )}

          {!_.isEmpty(params.sources) && (
            <HistorySourceFilter
              materialPlaceholder="Source"
              value={params.sources}
              onChange={({ target: { value } }) => handleApplyFilters({ sources: value })}
            />
          )}

          <MemberFiltersBar filters={params} onChange={handleApplyFilters} />
          <ClientFiltersBar filters={params} onChange={handleApplyFilters} />
          <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />

          <YesNoFilter
            icon="gear"
            placeholder="Show Notes"
            value={params.showNotes}
            onChange={({ target: { value } }) => handleApplyFilters({ showNotes: value })}
          />
        </Report.FiltersBar>

        {(() => {
          switch (query.status) {
            case 'loading':
            case 'filtering':
              return <PageLoader />;

            default:
              return (
                <>
                  <Data
                    query={query}
                    params={params}
                    onLoadMore={loadMore}
                    onSort={handleSort}
                    onRowClick={handleRowClick}
                    showNotes={params.showNotes}
                  />
                  <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
                </>
              );
          }
        })()}
      </Report>

      {dialog}
    </>
  );
}

function Data({ query, onLoadMore, onSort, onRowClick, showNotes }) {
  const { report, params } = query;
  const features = useFeatures();
  const { workspace } = useWorkspace();

  return (
    <Report.Table>
      <Table>
        <Table.Header>
          <Table.Column name="eventTime" width="12rem" onSort={onSort} sort={params.sort}>
            Event Time
          </Table.Column>
          <Table.Column name="entity" width="10rem" onSort={onSort} sort={params.sort}>
            Entity
          </Table.Column>
          <Table.Column minWidth="16rem">Details</Table.Column>
          <Table.Column name="statusId" width="10rem" onSort={onSort} sort={params.sort}>
            Event
          </Table.Column>
          <Table.Column width="20rem">Action By</Table.Column>
          <Table.Column width="12rem">Source</Table.Column>
        </Table.Header>

        <Table.Body fade={query.loading.table}>
          {report.records.results.map((item) => {
            const hasNotes = showNotes === 'yes' && !!item.notes;
            let isClickable = true;

            if (item.statusId === 'deleted') {
              isClickable = false;
            } else {
              switch (item.entity) {
                case 'allocation':
                  if (!features.allocations) isClickable = false;
                  break;
                case 'expense_item':
                  if (!features.expenseReporting) isClickable = false;
                  break;
                case 'client_approval':
                  if (!features.clientApprovals || !item.project?.enableClientApprovals) isClickable = false;
                  break;
                case 'workspace':
                  isClickable = false;
                  break;
                default:
                  break;
              }
            }

            const handleRowClick = () => onRowClick(item);

            return (
              <Table.Row
                style={{ flexWrap: 'wrap' }}
                onClick={isClickable ? () => handleRowClick() : null}
                key={item.id}>
                <Table.Cell>
                  <DateTime value={item.eventTime} format="l LT" />
                </Table.Cell>
                <Table.Cell>{item.entityName}</Table.Cell>
                {(() => {
                  if (item.entity === 'invoice' && item.statusId === 'deleted' && showNotes === 'no') {
                    return (
                      <Table.Cell>
                        <DetailsInfo>View notes for more details</DetailsInfo>
                      </Table.Cell>
                    );
                  }

                  if (item.statusId !== 'deleted')
                    switch (item.entity) {
                      case 'allocation':
                        return (
                          <Table.Cell>
                            <DetailsInfo>
                              {item.allocation.assignmentTypeId === 'project' ? (
                                <>
                                  <small>
                                    Project:&nbsp;
                                    <ProjectLink project={item.project} onClick={(e) => e.stopPropagation()} />
                                  </small>
                                  <small>
                                    Client:&nbsp;
                                    <ClientLink client={item.project.client} onClick={(e) => e.stopPropagation()} />
                                    {item.project.client.isInternal && <InternalClientTooltip />}
                                  </small>
                                </>
                              ) : (
                                <small>Time Off Type: {item.allocation.timeOffType.name}</small>
                              )}
                              {item.allocation.resourceTypeId === 'member' ? (
                                <small>Member: {item.allocation.member.name}</small>
                              ) : (
                                <small>Resource Placeholder: {item.allocation.placeholder.name}</small>
                              )}
                            </DetailsInfo>
                          </Table.Cell>
                        );
                      case 'client':
                        return (
                          <Table.Cell>
                            <DetailsInfo>
                              <small>
                                Client:&nbsp;
                                <ClientLink client={item.client} onClick={(e) => e.stopPropagation()} />
                              </small>
                            </DetailsInfo>
                          </Table.Cell>
                        );
                      case 'credit_note':
                        return (
                          <Table.Cell>
                            <DetailsInfo>
                              <small>
                                Credit Note:&nbsp;
                                <RouteLink
                                  to={`/app/${workspace.key}/billing/credit-notes/${item.creditNote.id}`}
                                  onClick={(e) => e.stopPropagation()}>
                                  #{item.creditNote.number}
                                </RouteLink>
                              </small>
                              <small>
                                Client:&nbsp;
                                <ClientLink client={item.client} onClick={(e) => e.stopPropagation()} />
                                {item.client.isInternal && <InternalClientTooltip />}
                              </small>
                            </DetailsInfo>
                          </Table.Cell>
                        );
                      case 'expense_item':
                        return (
                          <Table.Cell>
                            <DetailsInfo>
                              {item.project ? (
                                <>
                                  <small>
                                    Project:&nbsp;
                                    <ProjectLink project={item.project} onClick={(e) => e.stopPropagation()} />
                                  </small>
                                  <small>
                                    Client:&nbsp;
                                    <ClientLink client={item.project.client} onClick={(e) => e.stopPropagation()} />
                                    {item.project.client.isInternal && <InternalClientTooltip />}
                                  </small>
                                </>
                              ) : (
                                <small>Internal Expense</small>
                              )}
                              <small>Member: {item.expenseItem.member.name}</small>
                            </DetailsInfo>
                          </Table.Cell>
                        );
                      case 'invoice':
                        return (
                          <Table.Cell>
                            <DetailsInfo>
                              <small>
                                Invoice:&nbsp;
                                <RouteLink
                                  to={`/app/${workspace.key}/billing/invoices/${item.invoice.id}`}
                                  onClick={(e) => e.stopPropagation()}>
                                  #{item.invoice.number}
                                </RouteLink>
                              </small>
                              <small>
                                Client:&nbsp;
                                <ClientLink client={item.client} onClick={(e) => e.stopPropagation()} />
                                {item.client.isInternal && <InternalClientTooltip />}
                              </small>
                            </DetailsInfo>
                          </Table.Cell>
                        );
                      case 'member':
                        return (
                          <Table.Cell>
                            <DetailsInfo>
                              <small>
                                Member:&nbsp;
                                <MemberContactPopover member={item.member}>
                                  <RouteLink
                                    to={`/app/${workspace.key}/settings/members/details/${item.member.id}`}
                                    onClick={(e) => e.stopPropagation()}>
                                    {item.member.name}
                                  </RouteLink>
                                </MemberContactPopover>
                              </small>
                            </DetailsInfo>
                          </Table.Cell>
                        );
                      case 'project':
                      case 'client_approval':
                        return (
                          <Table.Cell>
                            <DetailsInfo>
                              <small>
                                Project:&nbsp;
                                <ProjectLink project={item.project} onClick={(e) => e.stopPropagation()} />
                              </small>
                              <small>
                                Client:&nbsp;
                                <ClientLink client={item.project.client} onClick={(e) => e.stopPropagation()} />
                                {item.project.client.isInternal && <InternalClientTooltip />}
                              </small>
                            </DetailsInfo>
                          </Table.Cell>
                        );
                      case 'project_task':
                        return (
                          <Table.Cell>
                            <DetailsInfo>
                              <small>
                                Task:&nbsp;
                                <RouteLink
                                  to={`/app/${workspace.key}/projects/${item.project.client.key}/${item.project.key}/tasks/${item.projectTask.number}/edit`}>
                                  {item.projectTask.name}
                                </RouteLink>
                              </small>
                              <small>
                                Project:&nbsp;
                                <ProjectLink project={item.project} onClick={(e) => e.stopPropagation()} />
                              </small>
                              <small>
                                Client:&nbsp;
                                <ClientLink client={item.project.client} onClick={(e) => e.stopPropagation()} />
                                {item.project.client.isInternal && <InternalClientTooltip />}
                              </small>
                            </DetailsInfo>
                          </Table.Cell>
                        );
                      case 'purchase_order':
                        return (
                          <Table.Cell>
                            <DetailsInfo>
                              <small>
                                Purchase Order:&nbsp;
                                <RouteLink
                                  to={`/app/${workspace.key}/clients/${item.client.key}/purchase-orders/${item.purchaseOrder.id}/edit`}>
                                  {item.purchaseOrder.name}
                                </RouteLink>
                              </small>
                              <small>
                                Client:&nbsp;
                                <ClientLink client={item.client} onClick={(e) => e.stopPropagation()} />
                                {item.client.isInternal && <InternalClientTooltip />}
                              </small>
                            </DetailsInfo>
                          </Table.Cell>
                        );
                      case 'security_role':
                        return (
                          <Table.Cell>
                            <DetailsInfo>
                              <small>
                                Security Role:&nbsp;
                                <RouteLink
                                  to={`/app/${workspace.key}/settings/security-roles/${item.securityRole.id}/edit`}
                                  onClick={(e) => e.stopPropagation()}>
                                  {item.securityRole.name}
                                </RouteLink>
                              </small>
                            </DetailsInfo>
                          </Table.Cell>
                        );
                      case 'time_entry':
                        return (
                          <Table.Cell>
                            <DetailsInfo>
                              {item.timeEntry.typeId === 'project_time' ? (
                                <>
                                  <small>
                                    Project:&nbsp;
                                    <ProjectLink project={item.project} onClick={(e) => e.stopPropagation()} />
                                  </small>
                                  <small>
                                    Client:&nbsp;
                                    <ClientLink client={item.project.client} onClick={(e) => e.stopPropagation()} />
                                    {item.project.client.isInternal && <InternalClientTooltip />}
                                  </small>
                                </>
                              ) : (
                                <small>Time Off Type: {item.timeEntry.timeOffType?.name}</small>
                              )}
                              <small>Member: {item.timeEntry.member?.name}</small>
                              {item.timeEntry.role && <small>Role: {item.timeEntry.role?.name}</small>}
                            </DetailsInfo>
                          </Table.Cell>
                        );

                      default:
                        return <Table.Cell />;
                    }
                  else {
                    return <Table.Cell />;
                  }
                })()}
                <Table.Cell>{item.status?.name}</Table.Cell>
                <Table.Cell>
                  {item.actionBy && (
                    <Member>
                      <MemberContactPopover member={item.actionBy} placement="left">
                        <Avatar value={item.actionBy} isCircle hasBackground initialsFontSize=".9rem" />
                      </MemberContactPopover>
                      <MemberInfo>{item.actionBy.name}</MemberInfo>
                    </Member>
                  )}
                </Table.Cell>
                <Table.Cell>{sources[item.sourceId]?.name}</Table.Cell>
                {hasNotes && <HistoryNotes>{item.notes.length === 500 ? `${item.notes}...` : item.notes}</HistoryNotes>}
              </Table.Row>
            );
          })}

          {report.records.total > report.records.results.length && <Table.Loader onIntersecting={onLoadMore} />}
        </Table.Body>

        <Table.Status
          total={report.records.total}
          label="Item"
          isLoading={query.status !== 'ready'}
          partial={report.partialResults}
        />
      </Table>
    </Report.Table>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <ReportPeriodFilter
        placeholder="Event Date Range"
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <HistoryEntityFilter
        materialPlaceholder="Entity"
        value={filters.entities}
        onChange={({ target: { value } }) => handleFilter({ entities: value })}
      />

      <HistoryEventFilter
        materialPlaceholder="Event"
        value={filters.events}
        onChange={({ target: { value } }) => handleFilter({ events: value })}
      />

      <HistorySourceFilter
        materialPlaceholder="Source"
        value={filters.sources}
        onChange={({ target: { value } }) => handleFilter({ sources: value })}
      />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />
      <ClientFiltersGroup filters={filters} onChange={handleFilter} />
      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}

export default History;
